import { colors, COLORS } from '@constants/colors';

// Styles for the redesigned map editor: the segment picker, the layout plaque, the walk banner and
// the drawings.
//
// Deliberately built out of the values the surrounding chrome already uses — the glass panel at a
// 14px radius, the `#4554E5` accent, the `#AEB6FF` lilac, the amber and green of the status dots —
// rather than a second look of its own. The Scene panel, its focus rail and the inspector belong to
// every feature in Editor Mode, not to the map editor, so none of that is restyled here and
// everything new has to sit inside it without announcing itself.

// The accent, and the two states it has. Named once: they appear on the root card, the variant card,
// the surface chip and the commit button, and a selected thing has to look selected the same way in
// all four.
const ACCENT = COLORS.MYTAVERSE_BLUE;
const ACCENT_HOVER = '#5865E8';
const ACCENT_SELECTED_BG = 'rgba(69, 84, 229, 0.18)';
const ACCENT_SELECTED_BORDER = 'rgba(69, 84, 229, 0.60)';
const LILAC = '#AEB6FF';

// The amber the redesign says "not saved" in — on the plaque's status dot, on the walk banner, and
// on the greybox marker. `COLORS.WARNING` is the app's error orange and reads as a fault; this is a
// state, and a layout nobody has published yet is not broken.
const AMBER = '#FFB800';
const AMBER_TEXT = '#FFD980';

// The glass every floating panel in Editor Mode is made of.
const GLASS = {
  background:
    'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 14px 40px rgba(0, 0, 0, 0.35)',
  borderRadius: '14px',
  color: COLORS.WHITE,
};

export const usePlanGlyphStyles = () => ({
  // 4:3 rather than 16:9. A module is a room seen from its doorway, and the taller frame is what
  // shows the ceiling and the floor — the two surfaces the finish menu can re-dress and the wide
  // frame crops away.
  picture: {
    position: 'relative',
    width: '100%',
    aspectRatio: '4 / 3',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pictureImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  // On the frame rather than beside it: the caption is about this picture, and a line of text under
  // the frame reads as a caption for the module instead.
  pictureNote: {
    position: 'absolute',
    left: '8px',
    bottom: '8px',
    padding: '3px 8px',
    borderRadius: '6px',
    backgroundColor: 'rgba(12, 12, 16, 0.72)',
    color: colors.white_60,
    fontSize: '10px',
    fontWeight: 500,
    letterSpacing: '0.02em',
    pointerEvents: 'none',
  },

  // The marker that says this slot is showing a stand-in. It goes in the corner rather than over the
  // middle because what it sits on is not a blank — the drawing under it carries the footprint and
  // the wall count, which is the whole reason the drawing was built instead of a grey rectangle.
  //
  // Small enough to survive the 48x36 root tile, which is the size that decided the shape: a caption
  // is unreadable there, and a slot with no marker at all was how "the kit ships no render for this
  // module" and "the render did not load" came to look identical.
  missingRenderSlot: {
    position: 'relative',
    display: 'inline-flex',
    lineHeight: 0,
  },
  missingRenderBadge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1px',
    borderRadius: '4px',
    backgroundColor: 'rgba(12, 12, 16, 0.72)',
    color: colors.white_60,
    pointerEvents: 'none',
  },
  missingRenderBadgeIcon: {
    fontSize: 11,
    display: 'block',
  },
  // On the large preview there is room to say it in words, so the badge sits where the caption does
  // and reads as one.
  missingRenderNote: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '3px 8px',
    borderRadius: '6px',
    backgroundColor: 'rgba(12, 12, 16, 0.72)',
    color: colors.white_60,
    fontSize: '10px',
    fontWeight: 500,
    letterSpacing: '0.02em',
    pointerEvents: 'none',
  },
});

// Three variant cards and their gaps, plus the picture above them. Wide enough for the roots list
// beside it without either column scrolling horizontally.
export const segmentPickerPaperSx = {
  ...GLASS,
  borderRadius: '16px',
  boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
  width: '100%',
  maxWidth: '900px',
};

// How wide one variant card is, and the gap between two. Exported because the carousel scrolls by
// exactly one card and the arrows have to step by the same amount the layout lays out by — a second
// copy of either number is how that goes out of step.
export const VARIANT_CARD_WIDTH_PX = 104;
export const VARIANT_CARD_GAP_PX = 8;
export const VARIANT_CARD_STEP_PX = VARIANT_CARD_WIDTH_PX + VARIANT_CARD_GAP_PX;

export const useSegmentPickerStyles = () => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px 10px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  },
  headerTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: COLORS.WHITE,
    whiteSpace: 'nowrap',
  },
  tabs: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    padding: '0 14px',
    borderRadius: '9px',
    color: colors.white_50,
    fontSize: '13.5px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': { color: COLORS.WHITE },
  },
  tabActive: {
    backgroundColor: 'rgba(69, 84, 229, 0.28)',
    color: COLORS.WHITE,
  },
  headerSpacer: {
    flex: 1,
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    height: '32px',
    padding: '0 10px',
    borderRadius: '9px',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    width: '190px',
    '&:focus-within': { borderColor: 'rgba(255, 255, 255, 0.28)' },
  },
  searchIcon: {
    fontSize: 15,
    color: colors.white_30,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: COLORS.WHITE,
    fontSize: '12.5px',
    fontFamily: 'inherit',
    '&::placeholder': { color: colors.white_30 },
  },
  closeButton: {
    color: colors.white_50,
    '&:hover': { color: COLORS.WHITE },
  },

  // Two columns: the root types, and the picture with its carousel. The left column is a list and
  // scrolls; the right one is fixed, because the picture is the thing being looked at.
  body: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '0px',
    padding: 0,
  },
  rootsColumn: {
    width: '276px',
    flexShrink: 0,
    borderRight: '1px solid rgba(255, 255, 255, 0.06)',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '460px',
    overflowY: 'auto',
  },
  rootCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    padding: '8px',
    borderRadius: '11px',
    border: '1px solid transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderColor: 'rgba(255, 255, 255, 0.14)',
    },
  },
  rootCardActive: {
    backgroundColor: ACCENT_SELECTED_BG,
    borderColor: ACCENT_SELECTED_BORDER,
  },
  rootGlyph: {
    width: '52px',
    height: '40px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '7px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  rootText: {
    minWidth: 0,
    flex: 1,
  },
  rootName: {
    fontSize: '13px',
    fontWeight: 600,
    color: COLORS.WHITE,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rootHint: {
    fontSize: '11px',
    color: colors.white_50,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // The greybox marker on a root whose modules the build cannot put on the ground. Dimmed rather
  // than disabled: UE greyboxes it, and placing one is a legitimate thing to do on a kit whose art
  // is not finished.
  rootWarning: {
    fontSize: 14,
    color: AMBER,
    flexShrink: 0,
  },
  noRoots: {
    padding: '18px 8px',
    textAlign: 'center',
  },
  noRootsText: {
    fontSize: '12.5px',
    color: colors.white_50,
  },
  noRootsAction: {
    marginTop: '8px',
    fontSize: '12.5px',
    fontWeight: 600,
    color: LILAC,
    cursor: 'pointer',
    '&:hover': { color: ACCENT_HOVER },
  },

  pickColumn: {
    flex: 1,
    minWidth: 0,
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  variantsLabel: {
    fontSize: '10.5px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: colors.white_50,
  },
  variantsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  variantsTrack: {
    display: 'flex',
    gap: `${VARIANT_CARD_GAP_PX}px`,
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    flex: 1,
    minWidth: 0,
    paddingBottom: '2px',
    // The track is the scroller, and a visible bar under three cards is noise — the arrows are the
    // affordance.
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  variantArrow: {
    color: colors.white_50,
    flexShrink: 0,
    '&:hover': { color: COLORS.WHITE },
  },
  variantCard: {
    flexShrink: 0,
    width: `${VARIANT_CARD_WIDTH_PX}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    padding: '6px',
    borderRadius: '9px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    cursor: 'pointer',
    transition:
      'background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.07)',
      borderColor: 'rgba(255, 255, 255, 0.24)',
      transform: 'translateY(-2px)',
    },
  },
  variantCardActive: {
    backgroundColor: 'rgba(69, 84, 229, 0.22)',
    borderColor: 'rgba(69, 84, 229, 0.65)',
  },
  variantGlyph: {
    width: '100%',
    height: '46px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  variantLabel: {
    fontSize: '11.5px',
    fontWeight: 600,
    color: COLORS.WHITE,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  variantHint: {
    fontSize: '10px',
    color: colors.white_50,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px 14px',
  },
  footerText: {
    flex: 1,
    minWidth: 0,
  },
  footerName: {
    fontSize: '13.5px',
    fontWeight: 600,
    color: COLORS.WHITE,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  footerMeta: {
    fontSize: '11.5px',
    color: colors.white_50,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // The way back to the entrance the host map bakes in. Quiet rather than accented: undoing a choice
  // is a real answer to the question, but it is not the one the author came here to give.
  secondaryButton: {
    height: '42px',
    padding: '0 16px',
    borderRadius: '11px',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: colors.white_70,
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      color: COLORS.WHITE,
    },
  },
  commitButton: {
    height: '42px',
    padding: '0 22px',
    borderRadius: '11px',
    border: `1px solid ${ACCENT_HOVER}`,
    backgroundColor: ACCENT,
    color: COLORS.WHITE,
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 8px 22px rgba(69, 84, 229, 0.35)',
    flexShrink: 0,
    '&:hover': { backgroundColor: ACCENT_HOVER },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderColor: 'rgba(255, 255, 255, 0.10)',
      color: colors.white_30,
      boxShadow: 'none',
    },
  },
});

export const useLayoutPlaqueStyles = () => ({
  // Under the Scene panel rather than inside it. These controls are about the whole layout, and the
  // panel above them is now about one segment at a time — putting Publish in a segment's panel is
  // what made the old one read as though it published that segment.
  root: {
    ...GLASS,
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '9px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  action: {
    flex: 1,
    height: '32px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: COLORS.WHITE,
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 0,
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&.Mui-disabled': {
      color: colors.white_20,
      borderColor: 'rgba(255, 255, 255, 0.10)',
    },
  },
  actionPrimary: {
    backgroundColor: ACCENT,
    borderColor: ACCENT_HOVER,
    fontWeight: 600,
    boxShadow: '0 6px 18px rgba(69, 84, 229, 0.35)',
    '&:hover': { backgroundColor: ACCENT_HOVER, borderColor: ACCENT_HOVER },
  },
  // The publish that would not fit, and the one the author has to act on rather than retry.
  oversize: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    padding: '7px 8px',
    borderRadius: '8px',
    border: `1px solid ${AMBER}55`,
    backgroundColor: 'rgba(255, 184, 0, 0.10)',
  },
  oversizeIcon: {
    fontSize: 14,
    color: AMBER,
    flexShrink: 0,
    marginTop: '1px',
  },
  oversizeText: {
    fontSize: '11px',
    lineHeight: 1.45,
    color: AMBER_TEXT,
  },
});

// The two states the plaque and the branding bar share, so "unsaved" is the same amber and the same
// words wherever the author reads it.
export const MAP_EDITOR_STATUS_COLORS = {
  draftDot: AMBER,
  draftText: AMBER_TEXT,
  liveDot: '#4ADE80',
  liveText: '#86EFAC',
  idleDot: colors.white_30,
  idleText: colors.white_60,
};
